import React, { useEffect, useState } from 'react';
import * as yup from 'yup';
import SubmitButton from '../../../../../Components/Button/SubmitButton';
import { v4 as uuidv4 } from 'uuid';
import FormWrapper from '../../../../../Components/Form/FormWrapper';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import InputField from '../../../../../Components/Form/components/InputField';
import SelectField from '../../../../../Components/Form/components/SelectField';
import { SelectValue } from '../../../../../Components/Base/Select/Select';
import useTranslation from '../../../../../Hooks/useTranslation';
import FormControl from '../../../../../Components/Form/FormControl';
import DiagnosisLatam from '../../../../../Components/Base/Diagnosis/DiagnosisLatam';

interface ElectronicPrescriptionDetailLatamAddMedicamentFormProps {
	onSubmit: (
		values: ElectronicPrescriptionDetailLatamAddMedicamentFormValues,
	) => void;
	medicament?: ElectronicPrescriptionDetailLatamAddMedicamentFormValues;
}

export interface ElectronicPrescriptionDetailLatamAddMedicamentFormValues {
	id: string;
	medicamentType?: string;
	medicine?: string;
	presentation?: number;
	doseFrequency?: string;
	doseFrequencyPer?: number;
	period?: string;
	periodPer?: number;
	concentration?: string;
	amount?: string;
	routeOfAdministration?: number;
	referredDiagnosis?: {
		code?: string;
		codeLabel?: string;
		type?: string;
	};
	referredDiagnosisId?: string;
	observations?: string;
	medicationCode?: string;
	formulaType?: string;
	rawMaterials?: {
		rawMaterial?: string;
		concentration?: string;
		amount?: string;
	};
	packIn?: string;
}

const PRESENTATION_OPTIONS: SelectValue[] = [
	{
		value: 1,
		label:
			'component.electronicPrescriptionDetailLatamAddMedicamentForm.presentationValue1',
	},
	{
		value: 2,
		label:
			'component.electronicPrescriptionDetailLatamAddMedicamentForm.presentationValue2',
	},
	{
		value: 3,
		label:
			'component.electronicPrescriptionDetailLatamAddMedicamentForm.presentationValue3',
	},
	{
		value: 4,
		label:
			'component.electronicPrescriptionDetailLatamAddMedicamentForm.presentationValue4',
	},
	{
		value: 5,
		label:
			'component.electronicPrescriptionDetailLatamAddMedicamentForm.presentationValue5',
	},
	{
		value: 6,
		label:
			'component.electronicPrescriptionDetailLatamAddMedicamentForm.presentationValue6',
	},
	{
		value: 7,
		label:
			'component.electronicPrescriptionDetailLatamAddMedicamentForm.presentationValue7',
	},
	{
		value: 8,
		label:
			'component.electronicPrescriptionDetailLatamAddMedicamentForm.presentationValue8',
	},
	{
		value: 9,
		label:
			'component.electronicPrescriptionDetailLatamAddMedicamentForm.presentationValue9',
	},
	{
		value: 10,
		label:
			'component.electronicPrescriptionDetailLatamAddMedicamentForm.presentationValue10',
	},
	{
		value: 11,
		label:
			'component.electronicPrescriptionDetailLatamAddMedicamentForm.presentationValue11',
	},
	{
		value: 12,
		label:
			'component.electronicPrescriptionDetailLatamAddMedicamentForm.presentationValue12',
	},
	{
		value: 13,
		label:
			'component.electronicPrescriptionDetailLatamAddMedicamentForm.presentationValue13',
	},
	{
		value: 14,
		label:
			'component.electronicPrescriptionDetailLatamAddMedicamentForm.presentationValue14',
	},
];

const DOSE_FREQUENCY_PER_OPTIONS: SelectValue[] = [
	{
		value: 0,
		label:
			'component.electronicPrescriptionDetailLatamAddMedicamentForm.doseFrequencyPerValue0',
	},
	{
		value: 1,
		label:
			'component.electronicPrescriptionDetailLatamAddMedicamentForm.doseFrequencyPerValue1',
	},
	{
		value: 2,
		label:
			'component.electronicPrescriptionDetailLatamAddMedicamentForm.doseFrequencyPerValue2',
	},
	{
		value: 3,
		label:
			'component.electronicPrescriptionDetailLatamAddMedicamentForm.doseFrequencyPerValue3',
	},
];

const PERIOD_PER_OPTIONS: SelectValue[] = [
	{
		value: 0,
		label:
			'component.electronicPrescriptionDetailLatamAddMedicamentForm.periodPerValue0',
	},
	{
		value: 1,
		label:
			'component.electronicPrescriptionDetailLatamAddMedicamentForm.periodPerValue1',
	},
	{
		value: 2,
		label:
			'component.electronicPrescriptionDetailLatamAddMedicamentForm.periodPerValue2',
	},
	{
		value: 3,
		label:
			'component.electronicPrescriptionDetailLatamAddMedicamentForm.periodPerValue3',
	},
	{
		value: 4,
		label:
			'component.electronicPrescriptionDetailLatamAddMedicamentForm.periodPerValue4',
	},
];

const ROUTE_OF_ADMINISTRATION_OPTIONS: SelectValue[] = [
	{
		value: 0,
		label:
			'component.electronicPrescriptionDetailLatamAddMedicamentForm.routeOfAdministrationValue0',
	},
	{
		value: 1,
		label:
			'component.electronicPrescriptionDetailLatamAddMedicamentForm.routeOfAdministrationValue1',
	},
	{
		value: 2,
		label:
			'component.electronicPrescriptionDetailLatamAddMedicamentForm.routeOfAdministrationValue2',
	},
	{
		value: 3,
		label:
			'component.electronicPrescriptionDetailLatamAddMedicamentForm.routeOfAdministrationValue3',
	},
	{
		value: 4,
		label:
			'component.electronicPrescriptionDetailLatamAddMedicamentForm.routeOfAdministrationValue4',
	},
	{
		value: 5,
		label:
			'component.electronicPrescriptionDetailLatamAddMedicamentForm.routeOfAdministrationValue5',
	},
	{
		value: 6,
		label:
			'component.electronicPrescriptionDetailLatamAddMedicamentForm.routeOfAdministrationValue6',
	},
	{
		value: 7,
		label:
			'component.electronicPrescriptionDetailLatamAddMedicamentForm.routeOfAdministrationValue7',
	},
	{
		value: 8,
		label:
			'component.electronicPrescriptionDetailLatamAddMedicamentForm.routeOfAdministrationValue8',
	},
	{
		value: 9,
		label:
			'component.electronicPrescriptionDetailLatamAddMedicamentForm.routeOfAdministrationValue9',
	},
	{
		value: 10,
		label:
			'component.electronicPrescriptionDetailLatamAddMedicamentForm.routeOfAdministrationValue10',
	},
	{
		value: 11,
		label:
			'component.electronicPrescriptionDetailLatamAddMedicamentForm.routeOfAdministrationValue11',
	},
	{
		value: 12,
		label:
			'component.electronicPrescriptionDetailLatamAddMedicamentForm.routeOfAdministrationValue12',
	},
	{
		value: 13,
		label:
			'component.electronicPrescriptionDetailLatamAddMedicamentForm.routeOfAdministrationValue13',
	},
	{
		value: 14,
		label:
			'component.electronicPrescriptionDetailLatamAddMedicamentForm.routeOfAdministrationValue14',
	},
	{
		value: 15,
		label:
			'component.electronicPrescriptionDetailLatamAddMedicamentForm.routeOfAdministrationValue15',
	},
	{
		value: 16,
		label:
			'component.electronicPrescriptionDetailLatamAddMedicamentForm.routeOfAdministrationValue16',
	},
	{
		value: 17,
		label:
			'component.electronicPrescriptionDetailLatamAddMedicamentForm.routeOfAdministrationValue17',
	},
	{
		value: 18,
		label:
			'component.electronicPrescriptionDetailLatamAddMedicamentForm.routeOfAdministrationValue18',
	},
];

const ElectronicPrescriptionDetailLatamAddMedicamentForm: React.FC<
	ElectronicPrescriptionDetailLatamAddMedicamentFormProps
> = ({ onSubmit, medicament }) => {
	const [defaultValues, setDefaultValues] = useState({
		medicamentType: '0',
		id: uuidv4(),
	});

	const [initialized, setInitialized] = useState(true);
	const { t } = useTranslation();

	useEffect(() => {
		formMethods.reset(medicament);
	}, [medicament]);

	// @ts-ignore
	const handleSubmit = async (values: any) => {
		setInitialized(false);

		onSubmit(values);
		formMethods.reset({ medicamentType: '0', id: uuidv4() });
		setInitialized(true);
	};

	const validationSchema = yup.object({
		medicamentType: yup
			.mixed()
			.required(
				t(
					'validation.electronicPrescriptionDetailLatamAddMedicamentForm.medicamentType',
				),
			),
		medicine: yup.mixed().when('medicamentType', {
			is: '0',
			then: yup
				.string()
				.required(
					t(
						'validation.electronicPrescriptionDetailLatamAddMedicamentForm.medicine',
					),
				),
		}),
		presentation: yup.object().shape({
			value: yup
				.number()
				.required(
					t(
						'validation.electronicPrescriptionDetailLatamAddMedicamentForm.presentation',
					),
				),
		}),
		doseFrequency: yup
			.string()
			.required(
				t(
					'validation.electronicPrescriptionDetailLatamAddMedicamentForm.doseFrequency',
				),
			),
		doseFrequencyPer: yup.object().shape({
			value: yup
				.number()
				.required(
					t(
						'validation.electronicPrescriptionDetailLatamAddMedicamentForm.doseFrequencyPer',
					),
				),
		}),
		period: yup
			.string()
			.required(
				t(
					'validation.electronicPrescriptionDetailLatamAddMedicamentForm.period',
				),
			),
		periodPer: yup.object().shape({
			value: yup
				.number()
				.required(
					t(
						'validation.electronicPrescriptionDetailLatamAddMedicamentForm.periodPer',
					),
				),
		}),
		concentration: yup
			.string()
			.required(
				t(
					'validation.electronicPrescriptionDetailLatamAddMedicamentForm.concentration',
				),
			),
		amount: yup
			.string()
			.required(
				t(
					'validation.electronicPrescriptionDetailLatamAddMedicamentForm.amount',
				),
			),
		routeOfAdministration: yup.object().shape({
			value: yup
				.number()
				.required(
					t(
						'validation.electronicPrescriptionDetailLatamAddMedicamentForm.routeOfAdministration',
					),
				),
		}),
		referredDiagnosis: yup.mixed().notRequired(),
		observations: yup.string().notRequired(),
		medicationCode: yup.string().notRequired(),
		formulaType: yup.mixed().when('medicamentType', {
			is: '1',
			then: yup
				.object()
				.nullable()
				.shape({
					value: yup
						.string()
						.required(
							t(
								'validation.electronicPrescriptionDetailLatamAddMedicamentForm.formulaType',
							),
						),
				}),
		}),
		rawMaterials: yup.mixed().when('medicamentType', {
			is: '1',
			then: yup.array().of(
				yup.object().shape({
					rawMaterial: yup
						.string()
						.required(
							t(
								'validation.electronicPrescriptionDetailLatamAddMedicamentForm.rawMaterials',
							),
						),
					concentration: yup
						.string()
						.required(
							t(
								'validation.electronicPrescriptionDetailLatamAddMedicamentForm.rawMaterials',
							),
						),
					amount: yup
						.string()
						.required(
							t(
								'validation.electronicPrescriptionDetailLatamAddMedicamentForm.rawMaterials',
							),
						),
				}),
			),
		}),
		packIn: yup.mixed().when('medicamentType', {
			is: '1',
			then: yup.string().notRequired(),
		}),
		id: yup.string().uuid().required(),
	});

	const formMethods = useForm({
		defaultValues: defaultValues,
		resolver: yupResolver(validationSchema),
	});

	return (
		<>
			{initialized && (
				<FormWrapper formMethods={formMethods}>
					<div className={'row'}>
						<div className={'col-xxl-5 col-lg-12 col-12'}>
							<InputField
								label='component.electronicPrescriptionDetailLatamAddMedicamentForm.medicineLabel'
								placeholder='component.electronicPrescriptionDetailLatamAddMedicamentForm.medicineLabel'
								name='medicine'
								isRequired
								isWrapped
							/>
						</div>
						<div className={'col-lg col-12'}>
							<SelectField
								label='component.electronicPrescriptionDetailLatamAddMedicamentForm.presentation'
								name='presentation'
								isRequired
								options={PRESENTATION_OPTIONS}
							/>
						</div>
						<div className={'col-lg col-12'}>
							<InputField
								label='component.electronicPrescriptionDetailLatamAddMedicamentForm.doseFrequency'
								name='doseFrequency'
								isRequired
								isWrapped
							/>
						</div>
						<div className={'col-lg col-12'}>
							<div style={{ height: '26px' }} />
							<SelectField
								name='doseFrequencyPer'
								isRequired
								options={DOSE_FREQUENCY_PER_OPTIONS}
							/>
						</div>
					</div>
					<div className={'row'}>
						<div className={'col-xxl col-lg-4 col-12'}>
							<InputField
								label='component.electronicPrescriptionDetailLatamAddMedicamentForm.period'
								name='period'
								isRequired
								isWrapped
							/>
						</div>
						<div className={'col-xxl col-lg-4 col-12'}>
							<div style={{ height: '26px' }} />
							<SelectField
								name='periodPer'
								isRequired
								options={PERIOD_PER_OPTIONS}
							/>
						</div>
						<div className={'col-xxl col-lg-4 col-12'}>
							<InputField
								label='component.electronicPrescriptionDetailLatamAddMedicamentForm.concentration'
								name='concentration'
								isRequired
								isWrapped
							/>
						</div>
						<div className={'col-xxl col-lg-4 col-12'}>
							<InputField
								label='component.electronicPrescriptionDetailLatamAddMedicamentForm.amount'
								name='amount'
								isRequired
								isWrapped
							/>
						</div>
						<div className={'col-xxl col-lg-4 col-12'}>
							<SelectField
								label='component.electronicPrescriptionDetailLatamAddMedicamentForm.routeOfAdministration'
								name='routeOfAdministration'
								isRequired
								options={ROUTE_OF_ADMINISTRATION_OPTIONS}
							/>
						</div>
					</div>
					<div className={'row'}>
						<div className='col-lg-5'>
							<FormControl
								label='component.electronicPrescriptionDetailLatamAddMedicamentForm.referredDiagnosis'
								name='referredDiagnosis'>
								<DiagnosisLatam showTypeSelection={false} />
							</FormControl>
						</div>
						<div className='col-lg-5'>
							<InputField
								label='component.electronicPrescriptionDetailLatamAddMedicamentForm.observations'
								name='observations'
								isWrapped
							/>
						</div>
					</div>
					<div className={'row'}>
						<div className={'col-lg-4'}>
							<InputField
								label='component.electronicPrescriptionDetailLatamAddMedicamentForm.medicationCode'
								name='medicationCode'
								isWrapped
							/>
						</div>
					</div>
					<div className={'row d-none'}>
						<div className={'col-lg-4'}>
							<InputField label='id' name='id' />
						</div>
					</div>
					<div className={'mt-4'}>
						<SubmitButton onSubmit={handleSubmit} title={'base.shared.add'} />
					</div>
				</FormWrapper>
			)}
		</>
	);
};

export default ElectronicPrescriptionDetailLatamAddMedicamentForm;
